/**
 * Collection Filters System - Moon Moon Agency v2.1
 * Shopify collection filtering with AJAX updates, custom events, and UI sync
 */

// Initialisation de l'objet queryParams pour stocker les paramètres de requête
Shopify.queryParams = {};

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const COLLECTION_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,

	// UI Settings
	defaultOpenFilters: 1,

	// Price Slider Settings
	priceGap: 10,

	// Selectors - Using data attributes for better separation of concerns
	selectors: {
		form: '[data-collection-form]',
		grid: '[data-collection-grid]',
		gridCollection: '[data-grid-collection]',
		shopifySection: '.shopify-section', // Keep as class (Shopify generated)
		activeFilters: '[data-active-filters]',
		reloadElements: '[data-collection-reload]',
		sortBy: '[data-sort-by]',
		sortByHidden: '[data-sort-by-hidden]',

		// Mobile filters
		mobileFilterWrapper: '[data-mobile-filters]',
		filterOverlay: '[data-filter-overlay]',
		filterInlineBtn: '[data-filter-inline-btn]',
		filterStickyBar: '[data-filter-sticky-bar]',
		stickyHeader: 'header.shopify-section',
		htmlElement: 'html',

		// Filter accordions
		filterHeaders: '[data-filter-header]',

		// Price sliders
		minRange: '[data-price-min]',
		maxRange: '[data-price-max]',
		sliderTrack: '[data-price-track]',
		minRangeValue: '[data-price-min-value]',
		maxRangeValue: '[data-price-max-value]',
		priceMinValue: '[data-price-min-display]',
		priceMaxValue: '[data-price-max-display]'
	}
};

/*********************************************************************************************
 * 🛠️ DOM SELECTORS CACHE
 */
const CollectionDOM = {
	get form() { return document.querySelector(COLLECTION_CONFIG.selectors.form); },
	get grid() { return document.querySelector(COLLECTION_CONFIG.selectors.grid); },
	get gridCollection() { return document.querySelector(COLLECTION_CONFIG.selectors.gridCollection); },
	get sortBySelector() { return document.querySelector(COLLECTION_CONFIG.selectors.sortBy); },
	get sortByHidden() { return document.querySelector(COLLECTION_CONFIG.selectors.sortByHidden); },
	get html() { return document.querySelector(COLLECTION_CONFIG.selectors.htmlElement); },
	get mobileFilterWrapper() { return document.querySelector(COLLECTION_CONFIG.selectors.mobileFilterWrapper); },
	get filterOverlay() { return document.querySelector(COLLECTION_CONFIG.selectors.filterOverlay); },
	get filterInlineBtn() { return document.querySelector(COLLECTION_CONFIG.selectors.filterInlineBtn); },
	get filterStickyBar() { return document.querySelector(COLLECTION_CONFIG.selectors.filterStickyBar); },

	get activeFilters() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters); },
	get reloadElements() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements); },
	get filterHeaders() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.filterHeaders); },
	get formInputs() { return this.form?.querySelectorAll('input') || []; },

	// Price sliders
	get priceSliders() {
		return {
			minRange: document.querySelector(COLLECTION_CONFIG.selectors.minRange),
			maxRange: document.querySelector(COLLECTION_CONFIG.selectors.maxRange),
			sliderTrack: document.querySelector(COLLECTION_CONFIG.selectors.sliderTrack),
			minRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.minRangeValue),
			maxRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.maxRangeValue)
		};
	},

	get shopifySection() {
		const gridElem = this.gridCollection;
		return gridElem?.closest(COLLECTION_CONFIG.selectors.shopifySection);
	}
};

/*********************************************************************************************
 * 🔌 CORE COLLECTION API
 */
const CollectionAPI = {
	async request(url) {
		window.emit('collection:api-start', { url });

		try {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Check if response is JSON (Section Rendering API) or HTML (fallback)
			const contentType = response.headers.get('content-type');
			const data = contentType && contentType.includes('application/json')
				? await response.json()
				: await response.text();

			window.emit('collection:api-success', { url, responseType: typeof data });
			return data;
		} catch (error) {
			window.emit('collection:api-error', { url, error: error.message });
			throw error;
		}
	},

	getSectionId() {
		const section = CollectionDOM.shopifySection;
		if (!section) {
			throw new Error('Collection grid element or shopify section not found');
		}
		return section.id;
	},

	getSectionIdShort() {
		return this.getSectionId().replace('shopify-section-', '');
	},

	async refreshWithFilters(url_filters) {
		window.emit('collection:filter-start', { filters: url_filters });

		try {
			const currentUrl = `${window.location.pathname}`;
			const sectionId = this.getSectionIdShort();

			// Use Shopify Section Rendering API
			const requestUrl = `${currentUrl}?${url_filters}&sections=${sectionId}`;
			const response = await this.request(requestUrl);

			if (!CollectionDOM.grid) {
				throw new Error('Collection grid element not found');
			}

			// Handle different response types
			let newSectionHtml;
			if (typeof response === 'object' && response[sectionId]) {
				// Section Rendering API JSON response
				newSectionHtml = response[sectionId];
			} else if (typeof response === 'string') {
				// Fallback: HTML response
				const newPageEl = document.createElement('DIV');
				newPageEl.innerHTML = response;
				const newSection = newPageEl.querySelector(COLLECTION_CONFIG.selectors.grid);
				if (!newSection) {
					throw new Error('Required collection elements not found in response');
				}
				newSectionHtml = newSection.outerHTML;
			} else {
				throw new Error('Invalid response format');
			}

			this.updateDOM(newSectionHtml, currentUrl, url_filters);
			window.emit('collection:filter-success', { filters: url_filters, url: `${currentUrl}?${url_filters}` });

		} catch (error) {
			window.emit('collection:filter-error', { error: error.message, filters: url_filters });

			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-loading');
			}

			// Fallback: reload page if AJAX fails
			window.location.search = url_filters;
		}
	},

	updateDOM(newSectionHtml, currentUrl, url_filters) {
		// Create temporary element to parse new HTML
		const tempEl = document.createElement('DIV');
		tempEl.innerHTML = newSectionHtml;

		// Update collection grid content (products only)
		const newGridCollection = tempEl.querySelector(COLLECTION_CONFIG.selectors.grid);
		if (newGridCollection && CollectionDOM.grid) {
			CollectionDOM.grid.innerHTML = newGridCollection.innerHTML;
		}

		// Sync disabled state of filter values (0 product) — the filter list itself is not re-rendered
		CollectionDOM.formInputs.forEach((input) => {
			if (!input.id) return;
			const newInput = tempEl.querySelector(`#${CSS.escape(input.id)}`);
			if (newInput) input.disabled = newInput.disabled;
		});

		// Update load more block (counter + button) — lives outside the product grid
		const newLoadMore = tempEl.querySelector('[data-load-more-wrapper]');
		const currentLoadMore = document.querySelector('[data-load-more-wrapper]');
		if (newLoadMore && currentLoadMore) {
			currentLoadMore.replaceWith(newLoadMore);
		} else if (newLoadMore && !currentLoadMore && CollectionDOM.gridCollection) {
			// Result spans multiple pages again: re-insert the load-more block after the grid
			CollectionDOM.gridCollection.appendChild(newLoadMore);
		} else if (currentLoadMore && !newLoadMore) {
			// Filtered result fits on one page: no more load-more block
			currentLoadMore.remove();
		}

		// Update active filters
		const newActiveFilters = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters);
		CollectionDOM.activeFilters.forEach((currentFilter, index) => {
			if (newActiveFilters[index]) {
				currentFilter.innerHTML = newActiveFilters[index].innerHTML;
			}
		});

		// Update other elements with 'data-collection-reload' attribute
		const newReloadElems = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements);
		CollectionDOM.reloadElements.forEach((currentElem, index) => {
			if (newReloadElems[index]) {
				// Check if element has a <span> child (like buttons from mm-btn)
				const currentSpan = currentElem.querySelector('span');
				const newSpan = newReloadElems[index].querySelector('span');

				if (currentSpan && newSpan) {
					// Update only the text inside the span
					currentSpan.textContent = newSpan.textContent;
				} else {
					// Fallback: update entire innerHTML for simple elements
					currentElem.innerHTML = newReloadElems[index].innerHTML;
				}
			}
		});

		// Update URL in history without reloading page
		const newUrl = `${currentUrl}?${url_filters}`;
		window.history.replaceState(null, document.title, newUrl);

		if (CollectionDOM.grid) {
			CollectionDOM.grid.classList.remove('mm-loading');
		}

		// Re-initialize filters, accordions and load more after DOM update
		CollectionFilters.init();
		FilterAccordions.init();
		if (window.LoadMoreManager) {
			const section = document.querySelector('[data-section-id]');
			if (section) window.LoadMoreManager.initSection(section);
		}

		// Re-initialize add to cart forms after DOM update
		if (typeof window.initAddToCartForms === 'function') {
			window.initAddToCartForms();
		}
	}
};

/*********************************************************************************************
 * 🔍 COLLECTION FILTERS MANAGEMENT
 */
const CollectionFilters = {
	init() {
		if (!CollectionDOM.form) {
			window.emit('collection:form-error', { error: 'Collection facets form not found' });
			return;
		}

		const inputs = CollectionDOM.formInputs;

		// Add event listeners to inputs
		inputs.forEach(input => {
			// Remove existing listeners to avoid duplicates
			input.removeEventListener('change', this.handleInputChange);
			input.addEventListener('change', this.handleInputChange);
		});

		window.emit('collection:filters-initialized', { inputCount: inputs.length });
	},

	handleInputChange(event) {
		window.emit('collection:input-change', {
			name: event.target.name || event.target.id,
			value: event.target.value,
			checked: event.target.checked
		});

		CollectionFilters.submitForm();
	},

	async submitForm() {
		if (!CollectionDOM.form || !CollectionDOM.grid) {
			window.emit('collection:form-error', { error: 'Required elements not found' });
			return;
		}

		try {
			window.emit('collection:form-submit-start');

			const formData = new FormData(CollectionDOM.form);
			const url_filters = new URLSearchParams(formData).toString();

			CollectionDOM.grid.classList.add('mm-loading');

			await CollectionAPI.refreshWithFilters(url_filters);

			window.emit('collection:form-submit-success', { filters: url_filters });

		} catch (error) {
			window.emit('collection:form-submit-error', { error: error.message });

			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-loading');
			}
		}
	}
};

/*********************************************************************************************
 * 📱 MOBILE FILTERS
 */
const MobileFilters = {
	_trapId: null,
	_trigger: null,

	isMobile() {
		return window.isTablet();
	},

	open(triggerBtn = null) {
		if (!this.isMobile()) return;

		const wrapperMobile = CollectionDOM.mobileFilterWrapper;
		const overlay = CollectionDOM.filterOverlay;
		const html = CollectionDOM.html;

		if (wrapperMobile && html) {
			wrapperMobile.classList.add('mm-open');
			wrapperMobile.setAttribute('role', 'dialog');
			wrapperMobile.setAttribute('aria-modal', 'true');
			overlay?.classList.add('mm-open');
			window.lockScroll();
			html.classList.add('mm-gorgias-hidden');
			document.querySelectorAll('[data-open-filters-mobile]').forEach(btn => btn.setAttribute('aria-expanded', 'true'));

			this._trigger = triggerBtn || document.activeElement;
			if (window.FocusTrapManager) {
				this._trapId = window.FocusTrapManager.activate(wrapperMobile, {
					trigger: this._trigger,
					onEscape: () => this.close()
				});
			}

			window.emit('collection:mobile-filters-opened');
		}
	},

	close() {
		if (!this.isMobile()) return;

		const wrapperMobile = CollectionDOM.mobileFilterWrapper;
		const overlay = CollectionDOM.filterOverlay;
		const html = CollectionDOM.html;

		if (wrapperMobile && html) {
			wrapperMobile.classList.remove('mm-open');
			wrapperMobile.removeAttribute('role');
			wrapperMobile.removeAttribute('aria-modal');
			overlay?.classList.remove('mm-open');
			window.unlockScroll();
			html.classList.remove('mm-gorgias-hidden');
			document.querySelectorAll('[data-open-filters-mobile]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));

			if (this._trapId && window.FocusTrapManager) {
				window.FocusTrapManager.deactivate(this._trapId);
				this._trapId = null;
				this._trigger = null;
			}

			window.emit('collection:mobile-filters-closed');
		}
	},

	checkDrawer() {
		if (!this.isMobile()) return;

		const hasOpenFilters = document.location.href.includes('open_filters');
		if (hasOpenFilters) {
			this.open();
		}
	}
};

/*********************************************************************************************
 * 📌 MOBILE FILTER STICKY BAR
 * Shows the fixed "Filtrer" bar once the inline filter button has scrolled under the sticky
 * header, as long as the collection grid is still in the viewport.
 */
const MobileFilterStickyBar = {
	_inlineBtnPassed: false,
	_gridInView: false,

	init() {
		const bar = CollectionDOM.filterStickyBar;
		const inlineBtn = CollectionDOM.filterInlineBtn;
		const grid = CollectionDOM.gridCollection;
		if (!bar || !inlineBtn || !grid || typeof IntersectionObserver !== 'function') return;

		const headerHeight = document.querySelector(COLLECTION_CONFIG.selectors.stickyHeader)?.offsetHeight || 0;

		new IntersectionObserver(([entry]) => {
			// Hidden only when scrolled past (above the viewport), not when not yet reached
			const rootTop = entry.rootBounds?.top ?? headerHeight;
			this._inlineBtnPassed = !entry.isIntersecting && entry.boundingClientRect.top < rootTop;
			this._update(bar);
		}, { rootMargin: `-${headerHeight}px 0px 0px 0px` }).observe(inlineBtn);

		new IntersectionObserver(([entry]) => {
			this._gridInView = entry.isIntersecting;
			this._update(bar);
		}).observe(grid);
	},

	_update(bar) {
		bar.classList.toggle('mm-open', this._inlineBtnPassed && this._gridInView);
	}
};

/*********************************************************************************************
 * 🎛️ FILTER ACCORDIONS
 */
const FilterAccordions = {
	_handleClick(event) {
		const header = event.currentTarget;
		header.classList.toggle('mm-active');
		const isActive = header.classList.contains('mm-active');
		header.setAttribute('aria-expanded', String(isActive));
		window.emit('collection:filter-accordion-toggle', { active: isActive });
	},

	init() {
		const filtersHeaders = CollectionDOM.filterHeaders;
		let openedCount = 0;

		filtersHeaders.forEach((filtersHeader) => {
			// Activate first filters by default (desktop only) — the mobile sort accordion stays closed
			const isSortHeader = filtersHeader.hasAttribute('data-sort-header');
			if (!isSortHeader && !window.isMobile() && openedCount < COLLECTION_CONFIG.defaultOpenFilters) {
				filtersHeader.classList.add('mm-active');
				filtersHeader.setAttribute('aria-expanded', 'true');
				openedCount++;
			}

			// Remove existing listener before adding to avoid duplicates on re-init
			filtersHeader.removeEventListener('click', FilterAccordions._handleClick);
			filtersHeader.addEventListener('click', FilterAccordions._handleClick);
		});

		window.emit('collection:accordions-initialized', { count: filtersHeaders.length });
	}
};

/*********************************************************************************************
 * 💰 PRICE SLIDERS
 */
const PriceSliders = {
	get elements() {
		return CollectionDOM.priceSliders;
	},

	isReady() {
		const elements = this.elements;
		return elements.minRange && elements.maxRange &&
				elements.sliderTrack && elements.minRangeValue &&
				elements.maxRangeValue;
	},

	init() {
		if (!this.isReady()) return;

		const { minRange, maxRange } = this.elements;

		minRange.addEventListener('input', () => {
			if (parseInt(minRange.value) > parseInt(maxRange.value) - COLLECTION_CONFIG.priceGap) {
				minRange.value = maxRange.value - COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});

		maxRange.addEventListener('input', () => {
			if (parseInt(maxRange.value) < parseInt(minRange.value) + COLLECTION_CONFIG.priceGap) {
				maxRange.value = parseInt(minRange.value) + COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});

		// Initial update
		this.updateRange();
		window.emit('collection:price-sliders-initialized');
	},

	updateRange() {
		if (!this.isReady()) {
			window.emit('collection:price-error', { error: 'Price slider elements not found' });
			return;
		}

		const { minRange, maxRange, sliderTrack, minRangeValue, maxRangeValue } = this.elements;

		const minValue = parseInt(minRange.value, 10);
		const maxValue = parseInt(maxRange.value, 10);

		// Update min and max values display
		const minValueEl = minRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMinValue);
		const maxValueEl = maxRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMaxValue);

		if (minValueEl) minValueEl.innerHTML = minValue;
		if (maxValueEl) maxValueEl.innerHTML = maxValue;

		// Calculate percentages for track background
		const minPercent = (minValue / minRange.max) * 100;
		const maxPercent = (maxValue / maxRange.max) * 100;

		// Update track background to reflect selected price range
		sliderTrack.style.background = `linear-gradient(to right, var(--black-200) ${minPercent}%, var(--black-900) ${minPercent}%, var(--black-900) ${maxPercent}%, var(--black-200) ${maxPercent}%)`;

		// Adjust width of min and max values on slider
		minRangeValue.style.width = `calc(${minPercent}%)`;
		maxRangeValue.style.width = `calc(${maxPercent}%)`;

		window.emit('collection:price-update', { minValue, maxValue, minPercent, maxPercent });
	}
};

/*********************************************************************************************
 * 🔄 SORT BY FUNCTIONALITY
 */
const SortBy = {
	init() {
		const sortBySelector = CollectionDOM.sortBySelector;
		if (!sortBySelector) {
			window.emit('collection:sort-error', { error: 'Sort by selector not found' });
			return;
		}

		sortBySelector.addEventListener('change', (e) => {
			const value = e.target.value;

			window.emit('collection:sort-change', { value, previousValue: Shopify.queryParams.sort_by });

			Shopify.queryParams.sort_by = value;
			location.search = new URLSearchParams(Shopify.queryParams).toString();
		});

		window.emit('collection:sort-initialized', { currentValue: sortBySelector.value });
	}
};

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS (for backward compatibility)
 */
window.mmSortByChanged = (select) => {
	const hiddenSelect = CollectionDOM.sortByHidden;
	if (!hiddenSelect || !select) {
		window.emit('collection:sort-error', { error: 'Sort by elements not found' });
		return;
	}

	hiddenSelect.value = select.value;
	CollectionFilters.submitForm();
};

/*********************************************************************************************
 * 📏 DESKTOP FILTER COLUMN HEIGHT
 * Tant que la colonne n'est pas encore collée (hero au-dessus), son haut est plus bas que
 * le top sticky : on expose sa position réelle pour que max-height s'arrête au bas de l'écran.
 */
const FilterColumnHeight = {
	_frame: null,

	update() {
		FilterColumnHeight._frame = null;
		const column = document.querySelector(COLLECTION_CONFIG.selectors.mobileFilterWrapper);
		if (!column || window.isTablet()) return;

		const stickyTop = parseFloat(getComputedStyle(column).top) || 0;
		const top = Math.round(Math.max(column.getBoundingClientRect().top, stickyTop));
		column.style.setProperty('--mm-filter-top', `${top}px`);
	},

	requestUpdate() {
		if (FilterColumnHeight._frame) return;
		FilterColumnHeight._frame = requestAnimationFrame(FilterColumnHeight.update);
	},

	init() {
		FilterColumnHeight.update();
		window.addEventListener('scroll', FilterColumnHeight.requestUpdate, { passive: true });
		window.addEventListener('resize', FilterColumnHeight.requestUpdate, { passive: true });
	}
};

// Mobile filter functions
window.mmOpenFiltersMobile = (trigger) => MobileFilters.open(trigger);
window.mmCloseFiltersMobile = () => MobileFilters.close();
window.mmCheckDrawer = () => MobileFilters.checkDrawer();

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	window.emit('collection:init-start');

	try {
		CollectionFilters.init();
		FilterAccordions.init();
		PriceSliders.init();
		SortBy.init();
		MobileFilters.checkDrawer();
		MobileFilterStickyBar.init();
		FilterColumnHeight.init();

		window.emit('collection:init-success');
	} catch (error) {
		window.emit('collection:init-error', { error: error.message });
		window.log('error', '🏷️ [COLLECTION]', 'Collection initialization failed', { error: error.message });
	}
});

/*********************************************************************************************
 * 🔢 FILTER ACTIVE COUNT
 */
const FilterActiveCount = {
	update() {
		const form = CollectionDOM.form;
		if (!form) return;

		form.querySelectorAll('[data-filter-header]').forEach(header => {
			const countEl = header.querySelector('[data-filter-active-count]');
			if (!countEl) return;

			const filterType = countEl.dataset.filterActiveCount;
			let count = 0;

			if (filterType === 'price_range') {
				const minInput = form.querySelector('[data-price-min]');
				const maxInput = form.querySelector('[data-price-max]');
				if (minInput && maxInput) {
					const isMinChanged = parseInt(minInput.value) > parseInt(minInput.min || 0);
					const isMaxChanged = parseInt(maxInput.value) < parseInt(maxInput.max);
					count = (isMinChanged || isMaxChanged) ? 1 : 0;
				}
			} else {
				const list = header.nextElementSibling;
				if (list) {
					count = list.querySelectorAll('input[type="checkbox"]:checked').length;
				}
			}

			countEl.textContent = count > 0 ? `(${count})` : '';
		});
	}
};

const FilterOpenBtnCount = {
	update() {
		const badge = document.querySelector('[data-filter-open-btn-count]');
		if (!badge) return;

		const form = CollectionDOM.form;
		if (!form) return;

		let total = 0;
		form.querySelectorAll('input[type="checkbox"]:checked').forEach(() => total++);

		const minInput = form.querySelector('[data-price-min]');
		const maxInput = form.querySelector('[data-price-max]');
		if (minInput && maxInput) {
			const isMinChanged = parseInt(minInput.value) > parseInt(minInput.min || 0);
			const isMaxChanged = parseInt(maxInput.value) < parseInt(maxInput.max);
			if (isMinChanged || isMaxChanged) total++;
		}

		if (total > 0) {
			badge.textContent = total;
			badge.classList.remove('mm-none');
		} else {
			badge.classList.add('mm-none');
		}
	}
};

/*********************************************************************************************
 * 🔄 SORT ACTIVE LABEL (mobile sort accordion header)
 */
const SortActiveLabel = {
	update() {
		const labelEl = document.querySelector('[data-sort-active-label]');
		const checked = CollectionDOM.form?.querySelector('input[name="sort_by"]:checked');
		if (!labelEl || !checked) return;

		const optionLabel = CollectionDOM.form.querySelector(`label[for="${checked.id}"]`);
		if (optionLabel) labelEl.textContent = optionLabel.textContent.trim();
	}
};

document.addEventListener('collection:filter-success', () => { FilterActiveCount.update(); FilterOpenBtnCount.update(); });
document.addEventListener('collection:input-change', () => { FilterActiveCount.update(); FilterOpenBtnCount.update(); SortActiveLabel.update(); });

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	// Filter close button
	const filterCloseBtn = document.querySelector('[data-filter-close]');
	if (filterCloseBtn) {
		filterCloseBtn.addEventListener('click', () => {
			if (typeof window.mmCloseFiltersMobile === 'function') {
				window.mmCloseFiltersMobile();
			}
		});
	}

	// Filter overlay click — close panel
	const filterOverlay = document.querySelector('[data-filter-overlay]');
	if (filterOverlay) {
		filterOverlay.addEventListener('click', () => {
			if (typeof window.mmCloseFiltersMobile === 'function') {
				window.mmCloseFiltersMobile();
			}
		});
	}

	// Filter submit button — close mobile panel (form submits automatically on input change)
	const filterSubmitBtn = document.querySelector('[data-filter-submit]');
	if (filterSubmitBtn) {
		filterSubmitBtn.addEventListener('click', () => {
			window.mmCloseFiltersMobile?.();
		});
	}

	// Open filters mobile buttons
	const openFiltersBtns = document.querySelectorAll('[data-open-filters-mobile]');
	openFiltersBtns.forEach((btn) => {
		btn.addEventListener('click', () => {
			if (typeof window.mmCloseMobileMenu === 'function') {
				window.mmCloseMobileMenu();
			}
			if (typeof window.mmOpenFiltersMobile === 'function') {
				window.mmOpenFiltersMobile(btn);
			}
		});
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	['init-success', 'filter-success', 'input-change', 'form-submit-start', 'price-update', 'sort-change'].forEach(event => {
		document.addEventListener(`collection:${event}`, e =>
			console.log(`🏷️ [COLLECTION] ${event}:`, e.detail)
		);
	});
}
